/**
 * Mulen Video Loader
 * -
 * Strategy:
 *   Hero video (.brand-hero):  poster shows instantly; video streams after LCP clears.
 *   All other videos:          sources stripped on parse; restored when 400px from viewport.
 *
 * Uses MutationObserver so interception happens AS elements are added to the DOM —
 * before the browser queues the network request. This is the only reliable way to
 * prevent autoplay video downloads without modifying server-side HTML.
 */

(function () {
  'use strict';

  var PRELOAD_MARGIN = '400px 0px'; // start loading before user reaches the video

  // ── Helpers ──────────────────────────────────────────────────────────────

  function captureSources(video) {
    var sources = video.querySelectorAll('source');
    var data = [];
    sources.forEach(function (s) {
      data.push({ src: s.getAttribute('src') || '', type: s.getAttribute('type') || '' });
      s.setAttribute('src', ''); // empty → browser cancels queued request
    });
    video.load(); // reset internal decoder so it stops trying
    return data;
  }

  function restoreSources(video, data) {
    var sources = video.querySelectorAll('source');
    sources.forEach(function (s, i) {
      if (data[i]) s.setAttribute('src', data[i].src);
    });
    video.setAttribute('autoplay', '');
    video.load();
    video.play().catch(function () {}); // ignore autoplay policy errors
  }

  function observeForPlayback(video, data) {
    if (!('IntersectionObserver' in window)) {
      restoreSources(video, data);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          restoreSources(entry.target, entry.target._mulenSrc || data);
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: PRELOAD_MARGIN });
    video._mulenSrc = data;
    io.observe(video);
  }

  // ── Hero video: defer until after LCP ────────────────────────────────────

  function deferHeroVideo(video) {
    var data = captureSources(video);

    function loadHero() {
      restoreSources(video, data);
    }

    // requestIdleCallback fires after browser is idle (post-LCP).
    // Fallback: 1.2s timeout — enough for LCP image to be painted.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadHero, { timeout: 2000 });
    } else {
      setTimeout(loadHero, 1200);
    }
  }

  // ── Process a single video element ───────────────────────────────────────

  function processVideo(video) {
    // Skip already-processed videos
    if (video._mulenProcessed) return;
    video._mulenProcessed = true;

    var inHero = !!video.closest('.brand-hero');

    if (inHero) {
      deferHeroVideo(video);
    } else {
      var data = captureSources(video);
      video.removeAttribute('autoplay'); // prevent browser from treating it as eager
      observeForPlayback(video, data);
    }
  }

  // ── MutationObserver: intercept videos as they're parsed ──────────────────

  var domObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // element nodes only
        if (node.tagName === 'VIDEO') {
          processVideo(node);
        } else {
          node.querySelectorAll('video').forEach(processVideo);
        }
      });
    });
  });

  // Start watching the whole document as early as possible
  domObserver.observe(document.documentElement, { childList: true, subtree: true });

  // Also process any videos already in the DOM when this script runs
  document.querySelectorAll('video').forEach(processVideo);

  // Disconnect after full load — all videos have been parsed by then
  window.addEventListener('load', function () {
    setTimeout(function () { domObserver.disconnect(); }, 500);
  });

})();
